import { SlateUtils } from '@all-hands-hq/plex-graphql-schema';
import { CircularProgress, createStyles, makeStyles, Theme } from '@material-ui/core';
import assertNever from 'assert-never';
import React, { useState } from 'react';

import {
  PostType,
  PublishStatus,
  useCreateEventMutation,
  useCreatePostMutation,
  useGetPlexDetailsQuery,
} from '../../../../graphql/gen/types';
import { spacings } from '../../../../theme';
import { notEmpty } from '../../../../utils/notEmpty';
import { SlateEditorValue } from '../../../shared/slate/SlateEditor';
import { PlexName } from '../../common/PlexName';
import { canPublishPost } from '../../permissions';
import { logAnalytics } from '../../shared/logAnalytics';
import { SplitButtonMenu } from '../../shared/SplitButtonMenu';
import { DialogActions } from '../DialogActions';
import { DialogTopBar } from '../DialogTopBar';
import { useDialogState } from '../useDialogState';
import { PostDialogContent, PostFormData, postFormDataToInput } from './PostDialogContent';

type Props = {
  type: 'post' | 'event';
  plexId: string;
};

function initialState(type: 'post' | 'event'): PostFormData {
  if (type === 'post') {
    return {
      type: PostType.Post,
      title: '',
      // Important to not memoize this
      body: SlateEditorValue.new(),
      entities: { expanded_link: [], mention: [] },
      primaryLinkId: null,
      labelIds: [],
      hasConcurrentUpdate: false,
    };
  } else {
    return {
      type: PostType.Event,
      title: '',
      // Important to not memoize this
      body: SlateEditorValue.new(),
      entities: { expanded_link: [], mention: [] },
      primaryLinkId: null,
      labelIds: [],
      eventDate: null,
      hasConcurrentUpdate: false,
    };
  }
}

export function CreatePostDialog({ type, plexId }: Props): React.ReactElement {
  const classes = useStyles();
  const [state, setState] = useState<PostFormData>(initialState(type));
  const { data: plexData } = useGetPlexDetailsQuery({ variables: { plexId } });
  const [createPostMutation, { loading: createPostLoading, error: createPostError }] = useCreatePostMutation();
  const [createEventMutation, { loading: createEventLoading, error: createEventError }] = useCreateEventMutation();
  const loading = createPostLoading || createEventLoading;
  const error = createPostError || createEventError;
  const [, setDialogState] = useDialogState();
  const fileUploading = SlateUtils.hasUploadsInProgress(state.body.value);

  const handleClose = (): void => {
    setDialogState(null);
  };

  const handleSave = async (publishStatus: PublishStatus): Promise<void> => {
    let postId: string | undefined;
    switch (state.type) {
      case PostType.Post: {
        const res = await createPostMutation({
          variables: {
            post: {
              ...postFormDataToInput(state),
              plexId,
              publishStatus,
            },
          },
          update: (cache) => {
            cache.evict({ fieldName: 'posts' });
          },
        });
        postId = res.data?.createPost?.id;
        break;
      }
      case PostType.Event: {
        if (!state.eventDate) {
          alert('Missing event date');
          return;
        }
        const res = await createEventMutation({
          variables: {
            event: {
              ...postFormDataToInput(state),
              plexId,
              publishStatus,
              eventDate: state.eventDate,
            },
          },
          update: (cache) => {
            cache.evict({ fieldName: 'posts' });
          },
        });
        postId = res.data?.createEvent?.id;
        break;
      }
      default:
        assertNever(state);
    }

    logAnalytics({ name: 'post created', details: { plexId, itemId: postId } });
    handleClose();
  };

  const handleCreate = (): void => {
    handleSave(PublishStatus.Internal);
  };

  const handleCreateDraft = (): void => {
    handleSave(PublishStatus.Draft);
  };

  const handlePublish = (): void => {
    handleSave(PublishStatus.Published);
  };

  if (!plexData?.loggedIn?.plexById) {
    return <CircularProgress size={24} />;
  }

  const canPublish = canPublishPost({
    plexRelation: plexData.loggedIn.plexById.currentUserRelation.state,
    status: PublishStatus.Internal,
  });

  // FIXME: Dialog usage causes a console warning, more details in https://github.com/mui-org/material-ui/issues/13394
  return (
    <div className={classes.root}>
      <DialogTopBar>
        <PlexName
          name={plexData.loggedIn.plexById.name}
          color={plexData.loggedIn.plexById.color}
          className={classes.plexName}
        />
      </DialogTopBar>

      <PostDialogContent
        post={state}
        onPostChange={setState}
        dialogTitle={type === 'post' ? 'Create Post' : 'Create Event'}
      />

      <DialogActions
        cta={
          <SplitButtonMenu
            disabled={fileUploading || loading || error != null}
            color="primary"
            variant="contained"
            options={[
              canPublish ? { onClick: handlePublish, label: 'Publish' } : null,
              { onClick: handleCreate, label: 'Post' },
              { onClick: handleCreateDraft, label: 'Create draft' },
            ].filter(notEmpty)}
          />
        }
      />
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    plexName: {
      marginLeft: spacings.xlarge,
      marginTop: spacings.normal,
      marginBottom: spacings.normal,
    },
  })
);
